import React, { useEffect, useMemo, useState } from "react";
import type { User } from "firebase/auth";
import { AccountChoice, AdminQuoteRow, adminCreateQuote, adminFetchAccounts, adminFetchQuotes, adminVoidQuote, formatDueDate, money } from "../utils/quotes";

const field = "w-full bg-white border border-slate-300 rounded-xl py-2 px-3";

const DEFAULT_TITLE = "ConferenceCaptioning.com - One Year License";
const DEFAULT_DESCRIPTION = "Extremely Accurate & Blazing Fast captions & translations for live events";
const EMPTY = { account: "", invoiceNumber: "", poNumber: "", title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION, amount: "", discountPercent: "0", dueDate: "" };

/** Admin only: pick an account, set the price and due date, and the customer sees that quote on their Billing page. */
const QuotesAdmin: React.FC<{ user: User; onChanged?: () => void }> = ({ user, onChanged }) => {
  const [accounts, setAccounts] = useState<AccountChoice[]>([]);
  const [rows, setRows] = useState<AdminQuoteRow[]>([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const load = () => {
    adminFetchQuotes(user).then(setRows).catch(() => setProblem("Could not load the quotes."));
  };
  useEffect(() => {
    load();
    adminFetchAccounts(user).then(setAccounts).catch(() => setProblem("Could not load the accounts to choose from."));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = useMemo(() => accounts.find((a) => a.email.toLowerCase() === form.account.trim().toLowerCase()), [accounts, form.account]);
  const amount = parseFloat(form.amount);
  const discount = parseInt(form.discountPercent, 10) || 0;
  const due = Number.isFinite(amount) ? Math.round(amount * (100 - discount)) : NaN; // cents

  const set = (key: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({ ...form, [key]: e.target.value });

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setProblem(null);
    setMessage(null);
    if (!chosen) { setProblem("Choose an account from the list (type its email)."); return; }
    setBusy(true);
    try {
      await adminCreateQuote(user, {
        uid: chosen.uid, email: chosen.email, invoiceNumber: form.invoiceNumber, poNumber: form.poNumber, title: form.title, description: form.description,
        amount, discountPercent: discount, dueDate: form.dueDate,
      });
      setMessage(`Quote made for ${chosen.email}. They will see it on their Billing page.`);
      setForm({ ...EMPTY, account: form.account }); // the same customer often gets a second quote for next year
      load();
      onChanged?.();
    } catch (err) {
      setProblem((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (row: AdminQuoteRow) => {
    if (!window.confirm(`Cancel the quote for ${row.email ?? row.uid} (${money(row.quote.netCents)}, due ${formatDueDate(row.quote.dueDate)})?`)) return;
    try {
      await adminVoidQuote(user, row.quote.id);
      setMessage("Quote cancelled.");
      load();
      onChanged?.();
    } catch (err) {
      setProblem((err as Error).message);
    }
  };

  return (
    <section className="bg-slate-100 rounded-2xl p-4 space-y-4" aria-label="Custom quotes">
      <h2 className="text-lg font-bold text-slate-900">Custom quotes</h2>
      <p className="text-sm text-slate-700">A customer with a quote sees only that quote on their Billing page instead of the two standard plans. A paid quote makes their account licensed.</p>

      <form onSubmit={create} className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label htmlFor="q-account" className="font-bold text-slate-900 block mb-1">Account</label>
          <input id="q-account" list="q-accounts" required className={field} value={form.account} onChange={set("account")} placeholder="Start typing their email" autoComplete="off" />
          <datalist id="q-accounts">{accounts.map((a) => <option key={a.uid} value={a.email}>{a.name ?? ""}</option>)}</datalist>
        </div>
        <div>
          <label htmlFor="q-number" className="font-bold text-slate-900 block mb-1">Invoice number</label>
          <input id="q-number" className={field} value={form.invoiceNumber} onChange={set("invoiceNumber")} placeholder="CC-1103-02" />
        </div>
        <div>
          <label htmlFor="q-po" className="font-bold text-slate-900 block mb-1">P.O./S.O. number</label>
          <input id="q-po" className={field} value={form.poNumber} onChange={set("poNumber")} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="q-title" className="font-bold text-slate-900 block mb-1">Title</label>
          <input id="q-title" required className={field} value={form.title} onChange={set("title")} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="q-description" className="font-bold text-slate-900 block mb-1">Description</label>
          <input id="q-description" className={field} value={form.description} onChange={set("description")} />
        </div>
        <div>
          <label htmlFor="q-amount" className="font-bold text-slate-900 block mb-1">Real price (USD)</label>
          <input id="q-amount" required type="number" min="1" step="0.01" className={field} value={form.amount} onChange={set("amount")} placeholder="4800" />
        </div>
        <div>
          <label htmlFor="q-discount" className="font-bold text-slate-900 block mb-1">Special discount (%)</label>
          <input id="q-discount" type="number" min="0" max="99" step="1" className={field} value={form.discountPercent} onChange={set("discountPercent")} />
        </div>
        <div>
          <label htmlFor="q-due" className="font-bold text-slate-900 block mb-1">Payment due</label>
          <input id="q-due" required type="date" className={field} value={form.dueDate} onChange={set("dueDate")} />
        </div>
        <div className="flex items-end">
          <p className="text-slate-800" aria-live="polite">
            {Number.isFinite(due) && due > 0 ? <>Customer pays <strong>{money(due)}</strong>{discount > 0 ? <span className="text-slate-600"> (real price {money(Math.round(amount * 100))})</span> : null}, plus the processing fee for how they pay.</> : "Enter the price."}
          </p>
        </div>
        <div className="sm:col-span-2 flex items-center gap-3">
          <button type="submit" className="btn-primary" disabled={busy}>{busy ? "Making..." : "Make quote"}</button>
          {message && <span className="text-sm text-emerald-800" role="status">{message}</span>}
          {problem && <span className="text-sm text-red-700" role="alert">{problem}</span>}
        </div>
      </form>

      {rows.length > 0 && (
        <table className="w-full text-sm bg-white rounded-xl overflow-hidden">
          <thead>
            <tr className="text-left text-slate-600"><th className="p-2 font-normal">Customer</th><th className="p-2 font-normal">Invoice</th><th className="p-2 font-normal text-right">Amount</th><th className="p-2 font-normal">Due</th><th className="p-2 font-normal">Status</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.quote.id} className="border-t border-slate-100">
                <td className="p-2">{r.email ?? r.uid}{r.mode === "test" && <span className="ml-1 text-xs font-bold text-amber-700">TEST</span>}</td>
                <td className="p-2">{r.quote.invoiceNumber ?? ""}</td>
                <td className="p-2 text-right">{money(r.quote.netCents)}</td>
                <td className="p-2">{formatDueDate(r.quote.dueDate)}</td>
                <td className={`p-2 font-bold ${r.quote.status === "paid" ? "text-emerald-700" : r.quote.status === "void" ? "text-slate-500" : "text-amber-700"}`}>{r.quote.status}</td>
                <td className="p-2 text-right">{r.quote.status === "open" && <button className="text-red-700 underline" onClick={() => cancel(r)}>Cancel</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

export default QuotesAdmin;
